import type { RootState } from "../app/store";
import type { OrderCartV2PayWithPointsAmount } from "../types/OrderCartV2Types";
import { selectLoyaltyCartResult } from "./loyaltyCartSelectors";
import { selectUseLoyaltyService } from "./loyaltyServiceSelectors";

export const selectPayWithPointsRequest = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? state.cartLoyaltyV2Reducer.loyalty?.payWithPoints
    : undefined;

export const selectPayWithPointsApplied = (
  state: RootState
): OrderCartV2PayWithPointsAmount | undefined =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.payWithPoints?.applied
    : undefined;

export const selectPayWithPointsMaximum = (
  state: RootState
): OrderCartV2PayWithPointsAmount | undefined =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.payWithPoints?.maximum
    : undefined;

export const selectIsPayWithPointsApplied = (state: RootState): boolean =>
  (selectPayWithPointsApplied(state)?.points ?? 0) > 0;

export const selectReceiptPayWithPointsAmount = (
  state: RootState
): number | undefined => {
  const receiptExtras = state.receipt?.payScreenReceipt?.receiptExtras;

  // ----------------------Core writes pointsDiscountAmount beside the totals; the nested field covers receipts persisted before that landed-----------------------
  return (
    receiptExtras?.pointsDiscountAmount ??
    receiptExtras?.loyaltyPoints?.redeemed?.payWithPointsAmount
  );
};

export const selectPayWithPointsReduction = (
  state: RootState
): { fromPoints: number; toPoints: number; amountLost: number } | undefined => {
  const request = selectPayWithPointsRequest(state);
  if (!request || request.useMaxPoints) {
    return undefined;
  }

  const applied = selectPayWithPointsApplied(state);
  const appliedPoints = applied?.points ?? 0;

  // ----------------------Nothing applied is a leftover request, not a reduction to zero — a persisted cart looks like this after sign-out-----------------------
  if (appliedPoints === 0 || appliedPoints >= request.requestedPoints) {
    return undefined;
  }

  const maximum = selectPayWithPointsMaximum(state);
  const rate =
    appliedPoints > 0 && applied
      ? applied.amount / appliedPoints
      : maximum?.points
      ? maximum.amount / maximum.points
      : 0;

  return {
    fromPoints: request.requestedPoints,
    toPoints: appliedPoints,
    amountLost: (request.requestedPoints - appliedPoints) * rate,
  };
};
